import { Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useLocation } from 'wouter';
import {
  SUPPORTED_SLUGS,
  type LocaleSlug,
  getSlugFromPath,
  stripLocaleFromPath,
  buildLocalePath,
} from '@/i18n/locale-utils';
import { languageNames, type SupportedLanguage } from '@/i18n';

const slugToLangCode: Record<LocaleSlug, SupportedLanguage> = {
  'en': 'en_US',
  'zh-hant': 'zh_TW',
  'zh-hans': 'zh_CN',
  'ja': 'ja',
  'ko': 'ko',
  'th': 'th',
  'vi': 'vi',
  'id': 'id',
  'es': 'es',
  'fr': 'fr',
  'de': 'de',
  'pt': 'pt',
  'it': 'it',
  'ru': 'ru',
  'tr': 'tr',
  'nl': 'nl',
  'ar': 'ar',
  'hi': 'hi',
  'ms': 'ms',
  'pl': 'pl',
};

export function LanguageSwitcher() {
  const currentSlug = getSlugFromPath(window.location.pathname) || 'en';
  const currentName = languageNames[slugToLangCode[currentSlug]];
  const [, navigate] = useLocation();

  const handleLanguageChange = (slug: LocaleSlug) => {
    if (slug === currentSlug) return;
    const subpath = stripLocaleFromPath(window.location.pathname);
    const target = buildLocalePath(slug, subpath);
    navigate(`~${target}`);
    window.location.assign(target);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          aria-label={`Language: ${currentName}`}
          className="gap-2 text-muted-foreground hover:text-foreground"
        >
          <Globe className="h-4 w-4" />
          <span className="hidden sm:inline">{currentName}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[140px] max-h-[400px] overflow-y-auto">
        {SUPPORTED_SLUGS.map((slug) => (
          <DropdownMenuItem
            key={slug}
            onClick={() => handleLanguageChange(slug)}
            className={currentSlug === slug ? 'bg-accent' : ''}
          >
            {languageNames[slugToLangCode[slug]]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
